import type { RVezyGisGeocode, RvezyGisPlace } from '~/types/api'
import type { IGooglePlace } from '~/types/common/google-place'
import type { GeocoderService } from '~/services/geocoder-service'

export function parseGooglePlace({
  place,
  geocode
}: {
  place?: google.maps.places.AutocompletePrediction
  geocode: RVezyGisGeocode
}): IGooglePlace {
  const formattedPlace: IGooglePlace = {
    searchable: true,
    placeId: geocode.place_id,
    center: geocode.geometry?.location,
    bounds: geocode.geometry?.bounds,
    mainText:
      place?.structured_formatting.main_text ??
      (geocode.address_components
        ?.filter((component) => ['street_number', 'route'].includes(component.types?.[0] ?? ''))
        .map((component) => component.long_name)
        .join(' ') ||
        'Unknown'),
    postalCode: geocode.address_components?.find((component) => component.types?.includes('postal_code'))?.long_name,
    secondaryText: place?.structured_formatting.secondary_text ?? '',
    types: place?.types ?? geocode?.types ?? [],
    fullName: '',
    place,
    geocode
  }

  geocode.address_components?.forEach((addrComponent) => {
    if (addrComponent.types?.includes('locality')) {
      formattedPlace.city = addrComponent
    } else if (addrComponent.types?.includes('administrative_area_level_1')) {
      formattedPlace.region = addrComponent
    } else if (addrComponent.types?.includes('country')) {
      formattedPlace.country = addrComponent
    }
  })

  formattedPlace.fullName =
    place?.description ??
    [formattedPlace.city?.long_name, formattedPlace.region?.short_name, formattedPlace.country?.long_name]
      .filter((x) => x)
      .join(', ')

  return formattedPlace
}

export function parseGooglePlaceFromGisPlaceResponse({
  place,
  geocode
}: {
  place?: RvezyGisPlace
  geocode: RVezyGisGeocode
}): IGooglePlace {
  const splittedAddress = place?.formatted_address?.split(',')
  const formattedPlace: IGooglePlace = {
    searchable: true,
    placeId: geocode.place_id,
    center: geocode.geometry?.location,
    bounds: geocode.geometry?.bounds,
    mainText:
      splittedAddress?.[0]?.trim() ??
      (geocode.address_components
        ?.filter((component) => ['street_number', 'route'].includes(component.types?.[0] ?? ''))
        .map((component) => component.long_name)
        .join(' ') ||
        'Unknown'),
    postalCode: geocode.address_components?.find((component) => component.types?.includes('postal_code'))?.long_name,
    secondaryText: splittedAddress?.slice(1).join().trim() ?? '',
    types: place?.types ?? geocode?.types ?? [],
    fullName: '',
    place,
    geocode
  }

  geocode.address_components?.forEach((addrComponent) => {
    if (addrComponent.types?.includes('locality')) {
      formattedPlace.city = addrComponent
    } else if (addrComponent.types?.includes('administrative_area_level_1')) {
      formattedPlace.region = addrComponent
    } else if (addrComponent.types?.includes('country')) {
      formattedPlace.country = addrComponent
    }
  })

  formattedPlace.fullName =
    place?.formatted_address ??
    [formattedPlace.city?.long_name, formattedPlace.region?.short_name, formattedPlace.country?.long_name]
      .filter((x) => x)
      .join(', ')

  return formattedPlace
}

export const searchPlaces = async (
  input: string,
  autocompleteService: google.maps.places.AutocompleteService,
  countryRestrictions: string[] = ['ca', 'us']
): Promise<google.maps.places.AutocompletePrediction[]> => {

  if (!input || !autocompleteService) return []

  try {
    const response = await autocompleteService.getPlacePredictions({
      input,
      componentRestrictions: {
        country: countryRestrictions
      }
    })

    return response.predictions
  } catch (e) {
    return []
  }
}

export const getGooglePlaceByName = async (
  input: string,
  autocompleteService: google.maps.places.AutocompleteService,
  geocoderService: GeocoderService
): Promise<IGooglePlace | undefined> => {
  const places = await searchPlaces(input, autocompleteService)
  if (!places.length) return

  const place = places[0]
  const geocode = await geocoderService.getGeocode(place.description)
  if (!geocode) return

  return parseGooglePlace({ place, geocode })
}

export const getCachedGooglePlaceByName = async (
  placeInput: string,
  geocoderService: GeocoderService
): Promise<IGooglePlace | undefined> => {
  const geocode = await geocoderService.getGeocode(placeInput)
  if (!geocode) return
  const place = await geocoderService.getPlaceDetails(geocode?.place_id)
  if (!place) return
  return parseGooglePlaceFromGisPlaceResponse({ place, geocode })
}
